import { createContext, useContext, useEffect } from 'react';
import { Button, Descriptions, Drawer, Form, InputNumber } from 'antd';
import { autorun, IObservableValue, observable } from 'mobx';
import { observer } from 'mobx-react-lite';

import { useInstance } from 'app/libs/use-instance';

import { TNewTariff, TTariff } from '../../core/tariff';
import { TariffDataMap } from './create-data-map-tariff';
import { DataGridTariffRow } from './create-table-grid-data';

type Context = {
  dataMap: TariffDataMap;
  showCellEditor: (
    cellData: Omit<DataGridTariffRow, 'tariffTypeName'> & { zoneId: number },
  ) => void;
  hideCellEditor: () => void;
};

const context = createContext(null as unknown as Context);

const Provider = context.Provider;

type EditCellProviderProps = {
  cellDataMap: TariffDataMap;
  children: React.ReactNode;
  onChange?: (tariff: TTariff) => void;
};

export const EditCellProvider = observer(
  ({ cellDataMap, onChange, children }: EditCellProviderProps) => {
    const drawerOpen = useInstance(() => observable.box(false));
    const $tariff = useInstance(() =>
      observable.box<TNewTariff | TTariff | null>(null),
    );

    const showCellEditor = (
      cellIds: Omit<DataGridTariffRow, 'tariffTypeName'> & { zoneId: number },
    ) => {
      drawerOpen.set(true);

      const tariff = cellDataMap.getRecord(cellIds);
      if (!tariff) return;

      $tariff.set(tariff);
    };

    const hideCellEditor = () => {
      drawerOpen.set(false);
      $tariff.set(null);
    };

    const handleSubmit = (tariff: TTariff) => {
      onChange && onChange(tariff);
      hideCellEditor();
    };

    return (
      <Provider
        value={{
          dataMap: cellDataMap,
          hideCellEditor,
          showCellEditor,
        }}
      >
        {children}
        <Drawer
          open={drawerOpen.get()}
          title="Редактирование тарифа"
          onClose={hideCellEditor}
          destroyOnClose
        >
          <TariffCellData $tariff={$tariff} />
          <div style={{ marginTop: 32 }}>
            <EditCellForm $tariff={$tariff} onSubmit={handleSubmit} />
          </div>
        </Drawer>
      </Provider>
    );
  },
);

type EditCellForm = {
  $tariff: IObservableValue<TNewTariff | TTariff | null>;
  onSubmit: (tariff: TTariff) => void;
};
const EditCellForm = observer(({ $tariff, onSubmit }: EditCellForm) => {
  const [form] = Form.useForm();

  const onFinish = () => {
    const values = form.getFieldsValue();
    const result = { ...$tariff.get(), ...values };

    onSubmit(result);
  };

  useEffect(() => {
    const disposer = autorun(() => {
      const tariff = $tariff.get();

      if (!tariff) return;
      const price = tariff.price;
      const maxCountPerTicket = tariff.maxCountPerTicket;

      form.setFieldsValue({ price, maxCountPerTicket });
    });

    return disposer();
  }, [$tariff, form]);

  return (
    <Form layout="vertical" form={form} onFinish={onFinish}>
      <Form.Item name="price" label="Цена">
        <InputNumber style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item name="maxCountPerTicket" label="Максимум в билете">
        <InputNumber style={{ width: '100%' }} />
      </Form.Item>

      <Button htmlType="submit" type="primary" block>
        Применить
      </Button>
    </Form>
  );
});

type TariffCellDataProps = {
  $tariff: IObservableValue<TNewTariff | TTariff | null>;
};

const TariffCellData = observer(({ $tariff }: TariffCellDataProps) => {
  if (!$tariff) return null;

  const tariff = $tariff.get();

  return (
    <Descriptions size="small" column={1} bordered>
      <Descriptions.Item label="Тип состава">
        {tariff?.trainType.nameRu}
      </Descriptions.Item>
      <Descriptions.Item label="Вид тарифа">
        {tariff?.tariffType.nameRu}
      </Descriptions.Item>
      <Descriptions.Item label="Зона">{tariff?.zone.nameRu}</Descriptions.Item>
    </Descriptions>
  );
});

export const useCellEditor = () => {
  const editor = useContext(context);

  if (!editor) {
    throw new Error(
      'useDataMapTariffs must be used within a DataMapTariffsProvider',
    );
  }

  return editor;
};
